<template>
    <div class="control-panel">
        <h2>Presenter Control Panel</h2>
        <br>

        <div v-if="!selectedShowId">
            <ShowSelectionDropdown :showsList="showsList" @selection-changed="handleShowSelectionChange" />
        </div>

        <div v-if="selectedShowId">
            <div>
                Selected Show: {{ showDescription }}
            </div>

            <br>

            <div :class="tilePreshowClass">
                <div>
                    <div>
                        <v-btn :disabled="btnPreshowDisabled" :color="btnPreshowColor" @click="onPreshowClicked">
                            Pre-Show
                        </v-btn>
                    </div>
                    <div> Display a QR code for the audience app. Audience members can upload photos before the show
                        starts.
                    </div>
                </div>
            </div>

            <div :class="tilePhotoSelectionClass">
                <div>
                    <div class="separateColumns">
                        <span style="padding-right:40px">
                            <v-btn :disabled="btnPhotoSelectionDisabled" :color="btnPhotoSelectionColor"
                                @click="onPhotoSelectionClicked">
                                Photo Selection
                            </v-btn>
                        </span>
                        <span style="padding-left:10px; min-width:100px;">
                            <ActSelectionDropdown @selection-changed="handleActSelectionChange" />
                        </span>
                        <span style="padding-left:10px; min-width:280px;">
                            <v-checkbox label="Character Photos Only" color="green" density="compact" hide-details
                                v-model="characterPhotosOnly">
                            </v-checkbox>
                        </span>
                        <span style="padding-left:10px; min-width:350px;">
                            <v-checkbox label="Practice Mode (don't retire photos)" color="green" density="compact"
                                hide-details v-model="practiceMode">
                            </v-checkbox>
                        </span>
                    </div>
                    <div> Randomly display photos for the audience to select by yelling 'STOP'.
                    </div>
                </div>
                <div class="separateColumns">

                    <!-- unclear why start rotation button doesn't line up with photo selection buton in the div above-->
                    <span style="padding-left:15px">
                        <v-btn v-if="!btnStartRotationDisabled" :disabled="btnStartRotationDisabled"
                            :color="btnStartRotationColor" @click="onStartRotationClicked">
                            Start Rotation
                        </v-btn>
                    </span>

                    <v-btn v-if="!btnStopRotationDisabled" :disabled="btnStopRotationDisabled"
                        :color="btnStopRotationColor" @click="onStopRotationClicked">
                        Stop Rotation
                    </v-btn>
                </div>
            </div>

            <div>
                Viewed:
                <div>
                    <span v-for="(photoUrl, index) in viewedPhotos" :key="index">
                        <img :src="photoUrl" class="photo-thumbnail" @click="onForceImageClicked(photoUrl)" />
                    </span>
                </div>
            </div>

        </div>

    </div>
</template>

<script>
import constants from '../../../photo-prov-shared/lib/classes/constants.mjs';
import { pProvShow } from "../../../photo-prov-shared/lib/classes/pprovShow.mjs"
import ActSelectionDropdown from './ActSelectionDropdown.vue';
import ShowSelectionDropdown from './ShowSelectionDropdown.vue';

const presenterStates = constants.PRESENTER_DISPLAY_STATES;
const photoRotationStates = constants.PHOTO_ROTATION_STATES;

export default {
    name: 'ControlPanel',
    data() {
        return {
            currentPresenterDisplayState: presenterStates.PRE_SHOW,
            currentPhotoRotationState: photoRotationStates.RESET,
            statePollingInterval: null,
            showsList: [],
            viewedPhotos: [],
            selectedShowId: null,
            selectedShowObj: null,
            characterPhotosOnly: false,
            practiceMode: true,
            errorMsg: null, // Added for error handling in getShowsData
        }
    },
    components: {
        ShowSelectionDropdown,
        ActSelectionDropdown
    },
    props: {
        authToken: {
            type: String,
            required: true
        },
    },
    watch: {
        characterPhotosOnly(newValue, oldValue) {
            // debug...
            console.log(`characterPhotosOnly was changed from ${oldValue} to ${newValue}`);
            localStorage.setItem(constants.PRESENTER_CHARACTER_PHOTOS_ONLY_KEY, newValue);
        },
        practiceMode(newValue, oldValue) {
            // debug...
            console.log(`practiceMode was changed from ${oldValue} to ${newValue}`);
            localStorage.setItem(constants.PRESENTER_PRACTICE_MODE_KEY, newValue);
        }
        //viewedPhotos(newValue) {
        // debug... console.log(`viewedPhotos length: ${newValue.length}`);
        //}
    },
    computed: {
        // Pre-show ----------------------------------------------------------------------------------------
        tilePreshowClass() {
            return this.currentPresenterDisplayState === presenterStates.PRE_SHOW ? 'selectedTile' : 'notSelectedTile';
        },
        btnPreshowColor() {
            return this.currentPresenterDisplayState === presenterStates.PRE_SHOW ? 'grey' : 'primary';
        },
        btnPreshowDisabled() {
            return this.currentPresenterDisplayState === presenterStates.PRE_SHOW;
        },

        // Photo select -------------------------------------------------------------------------------------
        tilePhotoSelectionClass() {
            return this.currentPresenterDisplayState === presenterStates.PHOTO_SELECT ? 'selectedTile' : 'notSelectedTile';
        },
        btnPhotoSelectionColor() {
            return this.currentPresenterDisplayState === presenterStates.PHOTO_SELECT ? 'grey' : 'primary';
        },
        btnPhotoSelectionDisabled() {
            return this.currentPresenterDisplayState === presenterStates.PHOTO_SELECT;
        },

        // Start Rotation ---------------------------------------------------------------------------
        btnStartRotationColor() {
            return this.btnStartRotationDisabled ? 'grey' : 'green';
        },
        btnStartRotationDisabled() {
            if (this.currentPresenterDisplayState === presenterStates.PHOTO_SELECT) {
                return this.currentPhotoRotationState === photoRotationStates.STARTED;
            }
            return true;
        },

        // Stop Rotation ---------------------------------------------------------------------------
        btnStopRotationColor() {
            return this.btnStopRotationDisabled ? 'grey' : 'orange';
        },
        btnStopRotationDisabled() {
            if (this.currentPresenterDisplayState === presenterStates.PHOTO_SELECT) {
                return this.currentPhotoRotationState !== photoRotationStates.STARTED;
            }
            return true;
        },

        // ---------------------------------------------------------------------------
        showDescription() {
            return this.selectedShowObj.showDate + ' -- ' + this.selectedShowObj.venue;
        },

    },
    created() {
        // start off in PRE-SHOW state
        this.setPresenterState(presenterStates.PRE_SHOW);
        localStorage.setItem(constants.PRESENTER_SELECTED_SHOW_KEY, null);
        localStorage.setItem(constants.PRESENTER_ROTATION_STATE_KEY, photoRotationStates.RESET);
        localStorage.setItem(constants.PRESENTER_CHARACTER_PHOTOS_ONLY_KEY, false);
        localStorage.setItem(constants.PRESENTER_PRACTICE_MODE_KEY, true);


        this.statePollingInterval = setInterval(() => {
            this.currentPresenterDisplayState = localStorage.getItem(constants.PRESENTER_DISPLAY_STATE_KEY);
            this.currentPhotoRotationState = localStorage.getItem(constants.PRESENTER_ROTATION_STATE_KEY);
            this.viewedPhotos = JSON.parse(localStorage.getItem(constants.PRESENTER_VIEWED_PHOTOS_KEY));
        }, 500);

        this.getShowsData();
    },
    mounted() {
    },
    methods: {
        handleActSelectionChange(selectedAct) {
            // debug...
            console.log(`switched to act ${selectedAct}`);

            // Act 2 - character photos only for character monologues
            this.characterPhotosOnly = (selectedAct === '2');
        },
        handleShowSelectionChange(selectedShowId) {
            // This method is called when the 'selection-changed' event is emitted by ShowSelectionDropdown
            console.log('Selected Show ID received in ControlPanel:', selectedShowId);
            this.selectedShowObj = this.showsList.find(show => show.id === selectedShowId);
            // debug... console.log('Selected Show Object:', this.selectedShowObj);
            this.selectedShowId = selectedShowId;

            // the presenter will pick up this value and load show data for the selected showId
            localStorage.setItem(constants.PRESENTER_SELECTED_SHOW_KEY, selectedShowId);
            localStorage.setItem(constants.PRESENTER_SHOW_DESC_KEY, this.showDescription);
        },
        onForceImageClicked(photoUrl) {
            // debug... console.log(`force image: ${photoUrl}`);
            localStorage.setItem(constants.PRESENTER_FORCED_URL_KEY, photoUrl);
            this.setPresenterState(presenterStates.FORCE_IMAGE);
        },
        onPreshowClicked() {
            this.setPresenterState(presenterStates.PRE_SHOW);
        },
        onPhotoSelectionClicked() {
            this.setPresenterState(presenterStates.PHOTO_SELECT);
        },
        onStartRotationClicked() {
            this.enablePhotoRotation(true);
        },
        onStopRotationClicked() {
            this.enablePhotoRotation(false);
        },
        setPresenterState(newState) {
            console.log(`setting presenter display state: ${newState}`);
            localStorage.setItem(constants.PRESENTER_DISPLAY_STATE_KEY, newState);
            if (newState === presenterStates.PHOTO_SELECT) {
                localStorage.setItem(constants.PRESENTER_ROTATION_STATE_KEY, photoRotationStates.RESET);
            }
        },
        enablePhotoRotation(enabled) {
            console.log(`setting photo rotation enabled: ${enabled}`);
            if (enabled) {
                localStorage.setItem(constants.PRESENTER_ROTATION_STATE_KEY, photoRotationStates.STARTED);
            }
            else {
                localStorage.setItem(constants.PRESENTER_ROTATION_STATE_KEY, photoRotationStates.STOPPED);
            }

        },

        async getShowsData() {
            const apiEndpoint = process.env.VUE_APP_PRV_API + '/shows';
            try {
                const response = await fetch(apiEndpoint, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${this.authToken}`,
                    }
                });

                if (!response.ok) {
                    // Handle HTTP errors (e.g., 4xx, 5xx)
                    const errorText = await response.text(); // Get error details if available
                    console.error('Fetch error:', response.status, errorText);
                    throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
                }

                const data = await response.json(); // Parse the JSON response body
                // debug... console.log(`fetch from ${apiEndpoint}\nresponse data:\n`, data);
                // sort array of shows by showDate ascending
                data.sort((a, b) => new Date(a.showDate) - new Date(b.showDate));

                // convert JSON to pProvShow objects
                this.showsList = data.map(item => new pProvShow(item));
                this.errorMsg = null; // Clear previous errors on success

            } catch (error) {
                // Handle network errors or errors during fetch/parsing
                console.error('Error fetching data:', error);
                this.errorMsg = 'Error getting data from server';
                this.showsList = []; // Clear shows data on error
            }
        },

    }

};
</script>

<style scoped>
.control-panel {
    border: 0px solid purple;
    padding: 10px;
}

.photo-thumbnail {
    max-width: 100px;
    object-fit: contain;
    padding: 10px;
    cursor: pointer;
}

.notSelectedTile,
.selectedTile {
    border-radius: 10px;
    padding: 10px;
    /* Added padding for better visual separation */
}

.selectedTile {
    border: 4px solid green;
}

.notSelectedTile {
    border: 2px solid #333;
}
</style>
